import {
  exportFileName,
  exportMaxSize,
  exportSize,
  exportWatermark,
  type FeatureFlags,
  legendHeight,
  type legendRows,
  legendStrip,
  measuredLegendRows,
  type Units,
  uid,
  type ViewMode,
} from '@better-lights/core';
import type { Pixels } from '../renderer/types.ts';
import { drawLegendStrip } from './legend.ts';
import { drawWatermark } from './watermark.ts';

export type ExportKind = 'demo' | 'lines';
export type ExportFormat = 'jpeg' | 'png';

export interface ExportImage {
  kind: ExportKind;
  blob: Blob;
  name: string;
  width: number;
  height: number;
}

export interface ExportInputs {
  render: (view: ViewMode) => Pixels;
  scene: Parameters<typeof legendRows>[0];
  labels: Parameters<typeof legendRows>[1];
  /** Measured length per line color (docs/spec/09 §1). */
  measuredFt?: Record<string, number> | null;
  currency: string;
  units: Units;
  legendEnabled: boolean;
  showLengths: boolean;
  format: ExportFormat;
  /** Plan flags of the project's workspace: Free = 1600 px cap + watermark (docs/spec/08 §1–2). */
  flags: FeatureFlags;
  date?: Date;
}

/** Applies the plan's export policy to a composed image: downscale (high quality), then watermark. */
export function applyExportPolicy(src: OffscreenCanvas, flags: FeatureFlags): OffscreenCanvas {
  const size = exportSize(src.width, src.height, exportMaxSize(flags));
  let out = src;
  if (size.scale < 1) {
    out = new OffscreenCanvas(size.width, size.height);
    const ctx = out.getContext('2d')!;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(src, 0, 0, size.width, size.height);
  }
  if (exportWatermark(flags)) drawWatermark(out.getContext('2d')!, out.width, out.height);
  return out;
}

/** Renders one export at full photo resolution (+ legend strip for Lines). docs/spec/03 §9. */
export async function renderExport(kind: ExportKind, inp: ExportInputs): Promise<ExportImage> {
  const px = inp.render(kind);
  const rows = kind === 'lines' && inp.legendEnabled ? measuredLegendRows(inp.scene, inp.labels, inp.measuredFt) : [];
  const legendH = rows.length > 0 ? legendHeight({ width: px.width }, rows.length) : 0;
  const W = px.width;
  const H = px.height + legendH;
  const canvas = new OffscreenCanvas(W, H);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error(`Could not create a ${W}×${H} drawing surface.`);
  ctx.putImageData(
    new ImageData(
      new Uint8ClampedArray(px.data.buffer as ArrayBuffer, px.data.byteOffset, px.data.byteLength),
      px.width,
      px.height,
    ),
    0,
    0,
  );
  if (legendH > 0) drawLegendStrip(ctx, W, px.height, legendStrip(rows, inp.currency, inp.showLengths, inp.units));
  const final = applyExportPolicy(canvas, inp.flags);
  const type = inp.format === 'png' ? 'image/png' : 'image/jpeg';
  const blob = await final.convertToBlob(inp.format === 'png' ? { type } : { type, quality: 0.92 });
  let name = exportFileName(kind, undefined, inp.date ?? new Date());
  if (inp.format === 'png') name = name.replace(/\.jpg$/, '.png');
  return { kind, blob, name, width: final.width, height: final.height };
}

export const newExportId = () => uid('exp');
